import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:refresh', this.#handleLegacyCartRefresh);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:refresh', this.#handleLegacyCartRefresh);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    // Respect explicit closed-cart intent, including background reward reconciliation.
    if (event.detail?.data?.openCart === false || event.detail?.data?.openMiniCart === false) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Bridges the cart refresh event used by LNGVTY's custom product sections
   * into Horizon's native cart update lifecycle. This keeps one shared drawer
   * across legacy, quiz, bundle, and standard product forms.
   * @param {CustomEvent} event
   */
  #handleLegacyCartRefresh = async (event) => {
    const sectionId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
    const shouldOpen = event.detail?.openMiniCart !== false;

    if (sectionId) {
      try {
        await sectionRenderer.renderSection(sectionId, { cache: false });
        const updatedDrawer = document.querySelector('cart-drawer-component');
        if (
          shouldOpen &&
          updatedDrawer instanceof CartDrawerComponent &&
          updatedDrawer.hasAttribute('auto-open')
        ) {
          updatedDrawer.open();
        }
        return;
      } catch (error) {
        console.warn('Unable to refresh the LNGVTY cart drawer section.', error);
      }
    }

    const cart = event.detail?.cart || {};
    document.dispatchEvent(
      new CartAddEvent(cart, 'lngvty-legacy-cart', {
        source: 'lngvty-legacy-cart',
        itemCount: cart.item_count,
      })
    );
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Keep this true even while the empty-cart markup is being replaced. Product
    // forms can open the drawer before the refreshed summary arrives; setting it
    // false during that gap leaves the new summary permanently unpinned.
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
